/**
 * Imperative toast store (react-hot-toast style): a tiny module-level pub/sub
 * plus the `toast` API. A single <Toaster /> mounted at the app root subscribes
 * and renders the stack. Because it's module-level, `toast.*` can be called
 * from anywhere — event handlers, `.js` files, non-component code — which is
 * how we replace native `window.confirm` / `window.alert` app-wide.
 *
 * Presentational styling is reused from `components/ui/toast.tsx`
 * (`toastVariants`, `ToastTitle`, `ToastDescription`) by the <Toaster />.
 */

export type ToastVariant = 'default' | 'success' | 'destructive' | 'warning'

export interface ToastItem {
	id: number
	kind: 'message' | 'confirm'
	variant: ToastVariant
	title?: string
	message: string
	/** Auto-dismiss delay in ms. 0 = persistent (confirm toasts never auto-dismiss). */
	duration: number
	confirmLabel?: string
	cancelLabel?: string
	/** Set for confirm toasts; resolved with the user's choice by <Toaster />. */
	resolve?: (value: boolean) => void
}

type Listener = (toasts: ToastItem[]) => void

let toasts: ToastItem[] = []
const listeners = new Set<Listener>()
let counter = 0

function emit() {
	for (const listener of listeners) listener(toasts)
}

export function subscribe(listener: Listener): () => void {
	listeners.add(listener)
	listener(toasts)
	return () => {
		listeners.delete(listener)
	}
}

export function getToasts(): ToastItem[] {
	return toasts
}

/**
 * Remove a toast. For a confirm toast dismissed without an explicit choice
 * (e.g. the close "X"), resolve its promise as `false` so awaiting callers
 * behave like a cancelled `window.confirm`.
 */
export function dismiss(id: number) {
	const item = toasts.find((t) => t.id === id)
	if (item?.kind === 'confirm') item.resolve?.(false)
	toasts = toasts.filter((t) => t.id !== id)
	emit()
}

/** Resolve a confirm toast with an explicit answer and remove it. */
export function answer(id: number, value: boolean) {
	const item = toasts.find((t) => t.id === id)
	if (item?.kind === 'confirm') item.resolve?.(value)
	toasts = toasts.filter((t) => t.id !== id)
	emit()
}

function push(item: Omit<ToastItem, 'id'>): number {
	const id = ++counter
	toasts = [...toasts, { ...item, id }]
	emit()
	return id
}

export interface MessageOptions {
	title?: string
	duration?: number
}

function message(variant: ToastVariant, msg: string, opts: MessageOptions = {}): number {
	const isPersistentKind = variant === 'destructive' || variant === 'warning'
	return push({
		kind: 'message',
		variant,
		title: opts.title,
		message: msg,
		duration: opts.duration ?? (isPersistentKind ? 7000 : 5000),
	})
}

export interface ConfirmOptions {
	title?: string
	message: string
	confirmLabel?: string
	cancelLabel?: string
	variant?: 'destructive' | 'warning' | 'default'
}

export const toast = {
	success: (msg: string, opts?: MessageOptions) => message('success', msg, opts),
	error: (msg: string, opts?: MessageOptions) => message('destructive', msg, opts),
	warning: (msg: string, opts?: MessageOptions) => message('warning', msg, opts),
	info: (msg: string, opts?: MessageOptions) => message('default', msg, opts),
	dismiss,
	/**
	 * Toast-based replacement for `window.confirm`. Returns a promise that
	 * resolves `true` on Confirm and `false` on Cancel / dismiss.
	 *
	 *   if (!(await toast.confirm('Delete this?'))) return
	 */
	confirm(input: string | ConfirmOptions): Promise<boolean> {
		const opts: ConfirmOptions = typeof input === 'string' ? { message: input } : input
		return new Promise<boolean>((resolve) => {
			push({
				kind: 'confirm',
				variant: opts.variant ?? 'destructive',
				title: opts.title ?? 'Please confirm',
				message: opts.message,
				confirmLabel: opts.confirmLabel ?? 'Confirm',
				cancelLabel: opts.cancelLabel ?? 'Cancel',
				duration: 0,
				resolve,
			})
		})
	},
}
